import { api, handleApiError } from '../client'
import type { PermissionLevel } from '../types'

/** External share invitation */
export interface ExternalShareInvitation {
  id: string
  templateId: string
  templateName: string
  email: string
  permissionLevel: PermissionLevel
  permissionLevelDisplay: string
  invitedByName: string
  personalMessage?: string
  expiresAt: string
  isAccepted: boolean
  acceptedAt?: string
  isExpired: boolean
  createdAt: string
}

/** Request to invite an external user */
export interface InviteExternalUserRequest {
  email: string
  permissionLevel: PermissionLevel
  personalMessage?: string
  expirationDays?: number
}

/** Invitation validation result */
export interface InvitationValidation {
  isValid: boolean
  message: string
  templateId?: string
  templateName?: string
  permissionLevel?: PermissionLevel
  permissionLevelDisplay?: string
  invitedByName?: string
  expiresAt?: string
}

/** Result of accepting an invitation */
export interface AcceptInvitationResult {
  templateId: string
  templateName: string
  redirectUrl: string
}

export const externalInvitationService = {
  /**
   * Sends an invitation to an external user to access a template
   */
  async inviteExternalUser(
    templateId: string,
    request: InviteExternalUserRequest
  ): Promise<ExternalShareInvitation> {
    try {
      const response = await api.post<ExternalShareInvitation>(
        `/v1/templates/${templateId}/invite-external`,
        request
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Validates an invitation token (public endpoint)
   */
  async validateInvitation(token: string): Promise<InvitationValidation> {
    try {
      const response = await api.get<InvitationValidation>(
        `/v1/invitations/${token}`
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Accepts an invitation (requires authentication)
   */
  async acceptInvitation(token: string): Promise<AcceptInvitationResult> {
    try {
      const response = await api.post<AcceptInvitationResult>(
        `/v1/invitations/${token}/accept`
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },
}
